import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ErrorMessages } from '@types';
import {
  scrollTop,
  dynamicDateErrorMessage,
  parseZodErrors,
  dateAndTimeRangeSchema,
  dateSchema,
  dynamicDateRangeErrorMessage,
  stringFieldSchema,
} from '@utils';

import {
  FormTitle,
  NavigationButtons,
  RadioOptions,
  TextInputMolecule,
} from '@molecules/*';
import { ErrorSummary, InlineErrorMessage } from '@atoms/*';
import { DateTemplate } from '../DateTemplate/DateTemplate';
import { SELECT_OPTIONS_ERROR } from '../constants/errors';
import { ZodError } from 'zod';

export const WorksCompleted = () => {
  const navigate = useNavigate();
  const { data, update, resetSelective } = usePlanningBreachStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string>('');
  const title = 'Have the works been completed?';
  const dateErrorPath = 'dateOfWorksCompleted';

  const resetErrorMessages = () => {
    setErrorMessages([]);
    setInlineErrorMessage('');
  };

  const options = [
    {
      id: 'Yes',
      title: 'Yes',
      labelText: 'Yes',
      isDefault: data.specialCharactarists === 'Yes',
    },
    {
      id: 'No',
      title: 'No',
      labelText: 'No',
      isDefault: data.specialCharactarists === 'No',
    },
    {
      id: 'DoNotKnow',
      title: 'DoNotKnow',
      labelText: 'I do not know',
      isDefault: data.specialCharactarists === 'DoNotKnow',
    },
  ];

  const handleChangeOptions = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as 'Yes' | 'No' | 'DoNotKnow';

    if (updatedSelection) {
      update('specialCharactarists', updatedSelection);
    } else {
      resetSelective();
    }
  };

  const dateDescription = (
    <div className="text-bodyLg text-dark-grey mb-4">
      <p className="font-bold">Give the date the works were completed </p>
      <p>For example, 31 01 2024</p>
    </div>
  );

  const handleDateInput = (e: FormEvent<HTMLInputElement>) => {
    resetErrorMessages();
    const { name, value } = e.currentTarget;

    const updatedDate = {
      ...data.worksCompletedDate,
      [name]: value,
    };
    update('worksCompletedDate', updatedDate);
  };

  const handleClick = () => {
    resetErrorMessages();
    try {
      stringFieldSchema(
        'specialCharactarists',
        SELECT_OPTIONS_ERROR.required.message,
      ).parse({
        specialCharactarists: data.specialCharactarists,
      });
      if (data.specialCharactarists === 'Yes') {
        dateSchema.parse(data.worksCompletedDate);
        dateAndTimeRangeSchema.parse({
          dayRange: data.worksCompletedDate.selectedDay,
          monthRange: data.worksCompletedDate.selectedMonth,
          yearRange: data.worksCompletedDate.selectedYear,
          minuteRange: 1, //To satisfy time validation
          hourRange: 1, //To satisfy time validation
        });
      }
      navigate(RoutesList.TELL_US_MORE_WORKS);
      return;
    } catch (error: any) {
      const validationErrors = parseZodErrors(error as ZodError);
      console.log({ validationErrors });
      setErrorMessages(
        validationErrors.map((error: ErrorMessages) => ({
          ...error,
          path: error.path,
        })),
      );
      const errors = parseZodErrors(error);
      const paths = errors.map((error) => error.path).sort();
      const dynamicInlineErrorMessage = dynamicDateErrorMessage(
        paths,
      ) as string;
      if (dynamicInlineErrorMessage) {
        setInlineErrorMessage(dynamicInlineErrorMessage);
        setErrorMessages(errors);
      } else {
        const newDateRangeErrorMessage = dynamicDateRangeErrorMessage(
          paths,
          errors,
        );
        if (newDateRangeErrorMessage) {
          setInlineErrorMessage(newDateRangeErrorMessage);
          setErrorMessages(errors);
        }
      }
    }
  };

  const buttonPrevious = {
    onClick: () => navigate(RoutesList.WORKS_DATE),
  };

  const buttonNext = {
    onClick: () => {
      handleClick();
      scrollTop();
    },
  };

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <ErrorSummary messages={errorMessages} />
        <FormTitle text={title} />
        <RadioOptions
          options={options}
          onChange={handleChangeOptions}
          errorPath={'specialCharactarists'}
          errorMessages={errorMessages}
        />
        {data.specialCharactarists === 'Yes' ? (
          <DateTemplate description={dateDescription}>
            <InlineErrorMessage
              path={dateErrorPath}
              messages={[{ path: dateErrorPath, message: inlineErrorMessage }]}
            />
            <div className="flex gap-x-4">
              <section className="w-12">
                <TextInputMolecule
                  maxLength={2}
                  label="Day"
                  name="selectedDay"
                  errorPath="daySelected"
                  value={data.worksCompletedDate.selectedDay}
                  onChange={(e) => handleDateInput(e)}
                />
              </section>
              <section className="w-12">
                <TextInputMolecule
                  maxLength={2}
                  label="Month"
                  name="selectedMonth"
                  errorPath="monthSelected"
                  value={data.worksCompletedDate.selectedMonth}
                  onChange={(e) => handleDateInput(e)}
                />
              </section>
              <section className="w-16">
                <TextInputMolecule
                  maxLength={4}
                  label="Year"
                  name="selectedYear"
                  errorPath="yearSelected"
                  value={data.worksCompletedDate.selectedYear}
                  onChange={(e) => handleDateInput(e)}
                />
              </section>
            </div>
          </DateTemplate>
        ) : null}
        <NavigationButtons
          buttonNext={buttonNext}
          buttonPrevious={buttonPrevious}
        />
      </div>
    </>
  );
};
